import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    Grid,
    Stack,
    Typography,
    Button,
    Divider,
    Alert,
    CircularProgress,
    useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

import PageContainer from '../../../components/container/PageContainer';
import CustomTextField from '../../../components/forms/theme-elements/CustomTextField';
import apiClient from '../../api/apiClient';
import { useAuthStore, useCampStore } from '../../stores';
import { useSnackbar } from 'notistack';
import { copyToClipboard } from '../../utils/clipboard';
import QRCode from '../../../components/ui/QRCode';

const CampCreatePage = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const setToken = useAuthStore((state) => state.setToken);
    const setCamp = useCampStore((state) => state.setCamp);

    const [formData, setFormData] = useState({
        campName: '',
        campCode: '',
        username: '',
        password: '',
        name: '',
        surname: ''
    });

    const [codeStatus, setCodeStatus] = useState(null);
    const [checkingCode, setCheckingCode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [createdCamp, setCreatedCamp] = useState(null);

    useEffect(() => {
        if (!formData.campCode || formData.campCode.length < 3) {
            setCodeStatus(null);
            return;
        }

        const timer = setTimeout(async () => {
            setCheckingCode(true);
            try {
                await apiClient.get(`/api/camps/public/by-code/${formData.campCode.toUpperCase()}`);
                setCodeStatus('taken');
            } catch (error) {
                if (error.response?.status === 404) {
                    setCodeStatus('available');
                } else {
                    setCodeStatus(null);
                }
            } finally {
                setCheckingCode(false);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [formData.campCode]);

    const handleChange = (field) => (e) => {
        let value = e.target.value;
        if (field === 'campCode') {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        }
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const isFormValid = () => {
        return formData.campName.trim() &&
            formData.campCode.length >= 3 &&
            codeStatus === 'available' &&
            formData.username.trim().length >= 3 &&
            formData.password.length >= 6 &&
            formData.name.trim() &&
            formData.surname.trim();
    };

    const handleSubmit = async () => {
        if (!isFormValid()) return;

        setSubmitting(true);
        try {
            const response = await apiClient.post('/api/camps/public/create', {
                campName: formData.campName.trim(),
                campCode: formData.campCode.toUpperCase(),
                owner: {
                    username: formData.username.trim(),
                    password: formData.password,
                    name: formData.name.trim(),
                    surname: formData.surname.trim()
                }
            });

            setToken(response.data.accessToken);
            sessionStorage.setItem('userId', response.data.userId);
            sessionStorage.setItem('refreshToken', response.data.refreshToken);
            setCamp(response.data.camp);

            setCreatedCamp({
                ...response.data.camp,
                inviteUrls: response.data.inviteUrls
            });

            enqueueSnackbar('Лагерь успешно создан!', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(
                error.response?.data?.message || 'Ошибка при создании лагеря',
                { variant: 'error' }
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = async (text, label) => {
        const success = await copyToClipboard(text);
        if (success) {
            enqueueSnackbar(`${label} скопирована`, { variant: 'success' });
        }
    };

    const handleGoToPanel = () => {
        navigate('/');
    };

    if (createdCamp) {
        return (
            <PageContainer title="Лагерь создан">
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: '100vh',
                        '&:before': {
                            content: '""',
                            background: theme.palette.background.auth,
                            backgroundSize: '400% 400%',
                            animation: 'gradient 15s ease infinite',
                            position: 'absolute',
                            height: '100%',
                            width: '100%',
                            opacity: '0.3',
                        },
                    }}
                >
                    <Grid container spacing={0} justifyContent="center" sx={{ py: 4 }}>
                        <Grid item xs={12} sm={10} md={8} lg={6}>
                            <Card elevation={9} sx={{ p: 4, zIndex: 1 }}>
                                <Stack spacing={3}>
                                    <Box textAlign="center">
                                        <CheckCircleIcon color="success" sx={{ fontSize: theme.typography.iconSizes.xxl }} />
                                        <Typography variant="h4" mt={2}>
                                            Лагерь "{createdCamp.name}" создан!
                                        </Typography>
                                    </Box>

                                    <Alert severity="info">
                                        Сохраните ссылки для приглашения организаторов и студентов
                                    </Alert>

                                    <Divider />

                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Ссылка для организаторов
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CustomTextField
                                                fullWidth
                                                value={createdCamp.inviteUrls?.org || ''}
                                                InputProps={{ readOnly: true }}
                                            />
                                            <Button
                                                variant="outlined"
                                                onClick={() => handleCopy(createdCamp.inviteUrls?.org, 'Ссылка')}
                                            >
                                                Копировать
                                            </Button>
                                        </Stack>
                                    </Box>

                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Ссылка для студентов
                                        </Typography>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CustomTextField
                                                fullWidth
                                                value={createdCamp.inviteUrls?.student || ''}
                                                InputProps={{ readOnly: true }}
                                            />
                                            <Button
                                                variant="outlined"
                                                onClick={() => handleCopy(createdCamp.inviteUrls?.student, 'Ссылка')}
                                            >
                                                Копировать
                                            </Button>
                                        </Stack>
                                        <Box mt={2} display="flex" justifyContent="center">
                                            <QRCode
                                                value={createdCamp.inviteUrls?.student || ''}
                                                size={180}
                                                title="QR-код для студентов"
                                            />
                                        </Box>
                                    </Box>

                                    <Divider />

                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        onClick={handleGoToPanel}
                                    >
                                        Перейти в панель управления
                                    </Button>
                                </Stack>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer title="Создание лагеря">
            <Box
                sx={{
                    position: 'relative',
                    minHeight: '100vh',
                    '&:before': {
                        content: '""',
                        background: theme.palette.background.auth,
                        backgroundSize: '400% 400%',
                        animation: 'gradient 15s ease infinite',
                        position: 'absolute',
                        height: '100%',
                        width: '100%',
                        opacity: '0.3',
                    },
                }}
            >
                <Grid container spacing={0} justifyContent="center" sx={{ py: 4 }}>
                    <Grid item xs={12} sm={10} md={8} lg={6}>
                        <Card elevation={9} sx={{ p: 4, zIndex: 1 }}>
                            <Stack spacing={3}>
                                <Box textAlign="center">
                                    <Typography variant="h4">
                                        Создание нового лагеря
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" mt={1}>
                                        Заполните данные для создания лагеря и вашего аккаунта владельца
                                    </Typography>
                                </Box>

                                <Divider />

                                <Typography variant="h6">Данные лагеря</Typography>

                                <CustomTextField
                                    label="Название лагеря"
                                    placeholder="Например: Солнечный лагерь"
                                    fullWidth
                                    value={formData.campName}
                                    onChange={handleChange('campName')}
                                />

                                <Box>
                                    <CustomTextField
                                        label="Код лагеря"
                                        placeholder="SUNNY2025"
                                        fullWidth
                                        value={formData.campCode}
                                        onChange={handleChange('campCode')}
                                        helperText="Только латинские буквы и цифры. Студенты будут использовать этот код для регистрации."
                                        InputProps={{
                                            endAdornment: checkingCode ? (
                                                <CircularProgress size={20} />
                                            ) : codeStatus === 'available' ? (
                                                <CheckCircleIcon color="success" />
                                            ) : codeStatus === 'taken' ? (
                                                <ErrorIcon color="error" />
                                            ) : null
                                        }}
                                        error={codeStatus === 'taken'}
                                    />
                                    {codeStatus === 'taken' && (
                                        <Typography variant="caption" color="error">
                                            Этот код уже занят
                                        </Typography>
                                    )}
                                </Box>

                                <Divider />

                                <Typography variant="h6">Данные владельца</Typography>

                                <CustomTextField
                                    label="Логин"
                                    placeholder="Минимум 3 символа"
                                    fullWidth
                                    value={formData.username}
                                    onChange={handleChange('username')}
                                />

                                <CustomTextField
                                    label="Пароль"
                                    type="password"
                                    placeholder="Минимум 6 символов"
                                    fullWidth
                                    value={formData.password}
                                    onChange={handleChange('password')}
                                />

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <CustomTextField
                                        label="Имя"
                                        fullWidth
                                        value={formData.name}
                                        onChange={handleChange('name')}
                                    />
                                    <CustomTextField
                                        label="Фамилия"
                                        fullWidth
                                        value={formData.surname}
                                        onChange={handleChange('surname')}
                                    />
                                </Stack>

                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    onClick={handleSubmit}
                                    disabled={!isFormValid() || submitting}
                                >
                                    {submitting ? <CircularProgress size={24} /> : 'Создать лагерь'}
                                </Button>

                                <Box textAlign="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Уже есть аккаунт?{' '}
                                        <Typography
                                            component="span"
                                            color="primary"
                                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={() => navigate('/login')}
                                        >
                                            Войти
                                        </Typography>
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </PageContainer>
    );
};

export default CampCreatePage;
